import { useState, useEffect } from "react";
import { FaMinus, FaPaperPlane, FaPlus } from "react-icons/fa";
import Button from "../../../common/Button";
import { MenuPermissionAPI } from "../../../utlis/apiService";
import InputField from "../../../common/InputField";
import { IoMdClose } from "react-icons/io";
import { AnimatePresence, motion } from "framer-motion";
import { MdClose } from "react-icons/md";

const MenuPermissionForm = ({
  isUserData,
  isMenuIDSData,
  onCancel,
  isEditing,
  onSubmit,
  formData,
}) => {
  const [userId, setUserId] = useState(null);
  const [permissions, setPermissions] = useState([
    { menuId: null, insert: false, update: false, delete: false, view: false },
  ]);
  const [localFormData, setLocalFormData] = useState({
    userId: "",
    menuId: [],
  });

  useEffect(() => {
    if (formData?.userId) {
      setUserId(formData.userId);
      setLocalFormData((prev) => ({
        ...prev,
        userId: formData.userId,
      }));
      setPermissions(
        formData.permissions?.length
          ? formData.permissions
          : [
              {
                menuId: null,
                insert: false,
                update: false,
                delete: false,
                view: false,
              },
            ],
      );
    }
  }, [formData]);

  const handleChange = (index, field, value) => {
    const updated = [...permissions];
    updated[index][field] = value;
    setPermissions(updated);
  };

  const handleAddRow = () => {
    setPermissions([
      ...permissions,
      {
        menuId: null,
        insert: false,
        update: false,
        delete: false,
        view: false,
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    const item = permissions[index];
    if (userId && item.menuId) {
      MenuPermissionAPI.deleteMenuId(userId, item.menuId).then(() => {
        const updated = [...permissions];
        updated.splice(index, 1);
        setPermissions(updated);
      });
    } else {
      const updated = [...permissions];
      updated.splice(index, 1);
      setPermissions(updated);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 z-[9999]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white border border-gray-300 w-full max-w-5xl h-[90vh] flex flex-col shadow-none"
        >
          <div className="bg-white border-b border-gray-100 text-gray-900 flex justify-between items-center px-5 py-4 shrink-0">
            <h2 className="text-[14px] font-black uppercase tracking-widest">
              {isEditing ? "Modify Menu Permissions" : "Set New Menu Permissions"}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <MdClose size={24} />
            </button>
          </div>
          
          <div className="p-5 overflow-y-auto flex-1 bg-white">
            <div className="bg-white border border-gray-200 p-4 mb-6">
              <InputField
                type="select"
                label="Target User / Operator"
                options={isUserData}
                value={localFormData.userId}
                onChange={(selectedValue) => {
                  setLocalFormData((prev) => ({
                    ...prev,
                    userId: selectedValue,
                  }));
                }}
                required
              />
            </div>

            <div className="bg-white border border-gray-200">
               <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Permission Matrix</h4>
               </div>
               <table className="w-full text-[13px] border-collapse">
                <thead className="bg-white">
                  <tr className="text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    <th className="px-4 py-3 border-r border-gray-50">Menu</th>
                    <th className="px-4 py-3 border-r border-gray-50 text-center">Insert</th>
                    <th className="px-4 py-3 border-r border-gray-50 text-center">Update</th>
                    <th className="px-4 py-3 border-r border-gray-50 text-center">Delete</th>
                    <th className="px-4 py-3 border-r border-gray-50 text-center">View</th>
                    <th className="px-4 py-3 text-center w-24">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {permissions.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 border-r border-gray-50 min-w-[250px]">
                        <InputField
                          type="select"
                          options={isMenuIDSData}
                          value={row.menuId}
                          onChange={(val) => handleChange(index, "menuId", val)}
                          placeholder="Select Menu"
                        />
                      </td>
                      {["insert", "update", "delete", "view"].map((perm) => (
                        <td className="px-4 py-2 border-r border-gray-50 text-center" key={perm}>
                          <input
                            type="checkbox"
                            checked={row[perm]}
                            className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                            onChange={() => handleChange(index, perm, !row[perm])}
                          />
                        </td>
                      ))}
                      <td className="px-4 py-2 text-center">
                        <div className="flex justify-center gap-1">
                          <button
                            onClick={() => handleRemoveRow(index)}
                            className="p-1.5 text-red-500 hover:bg-red-50 transition-colors"
                            title="Remove Row"
                          >
                            <FaMinus size={14} />
                          </button>
                          <button
                            onClick={handleAddRow}
                            className="p-1.5 text-primary hover:bg-blue-50 transition-colors"
                            title="Add Row Above"
                          >
                            <FaPlus size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-8 gap-3">
              <button
                className="px-6 py-2 border border-gray-300 text-gray-600 font-bold text-[12px] uppercase tracking-widest hover:bg-gray-50 transition-colors"
                onClick={onCancel}
              >
                Discard
              </button>

              <button
                className="px-8 py-2 bg-[#3F80F7] text-white font-bold text-[12px] uppercase tracking-[2px] hover:bg-[#0a2e58] border border-[#3F80F7] transition-colors flex items-center gap-2"
                onClick={() => {
                  const payload = permissions
                    .filter((p) => p.menuId)
                    .map((p) => ({
                      UserId: Number(localFormData.userId),
                      MenuId: Number(p.menuId),
                      IsInsert: p.insert,
                      IsUpdate: p.update,
                      IsDelete: p.delete,
                      IsView: p.view,
                    }));

                  onSubmit(payload);
                }}
              >
                {isEditing ? "Confirm Update" : "Commit Changes"}
                <FaPaperPlane size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>

  );
};

export default MenuPermissionForm;
